import axios from "axios";
import { ROUTE_LOGIN } from "./VMConstants";
import { clearAuthSession } from "./authSessionHelper";
import {
  buildAuthHeaders,
  getAuthToken,
  isAuthPagePath,
  isUnauthorizedPayload,
  isVenueManagerApiUrl,
  shouldAttachAuthHeader,
} from "./jwtAuth";

const instrumentedClients = new WeakSet();
let fetchPatched = false;
let handlingUnauthorized = false;
let originalFetch = null;
let unauthorizedHandler = defaultUnauthorizedHandler;

function resolveRequestUrl(config = {}) {
  const url = config.url || "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = config.baseURL || "";
  if (!base) return url;
  if (url.startsWith("/")) return `${String(base).replace(/\/+$/, "")}${url}`;
  return `${base}${url}`;
}

async function defaultUnauthorizedHandler() {
  if (handlingUnauthorized) return;
  handlingUnauthorized = true;

  try {
    await clearAuthSession();
  } catch (err) {
    console.error("Failed to clear session after 401", err);
  }

  if (typeof window === "undefined") {
    handlingUnauthorized = false;
    return;
  }

  const path = window.location?.pathname || "";
  if (!isAuthPagePath(path)) {
    window.location.assign(`/${ROUTE_LOGIN}`);
    return;
  }

  handlingUnauthorized = false;
}

export function setUnauthorizedHandler(handler) {
  unauthorizedHandler = handler || defaultUnauthorizedHandler;
}

export function resetUnauthorizedHandling() {
  handlingUnauthorized = false;
}

function applyAxiosAuthHeader(config) {
  const url = resolveRequestUrl(config);
  if (!shouldAttachAuthHeader(url)) {
    if (config.headers && typeof config.headers.delete === "function") {
      config.headers.delete("Authorization");
    } else if (config.headers) {
      delete config.headers.Authorization;
      delete config.headers.authorization;
    }
    return config;
  }

  const token = getAuthToken();
  if (!token) return config;

  const authHeader = `Bearer ${token}`;
  if (config.headers && typeof config.headers.set === "function") {
    config.headers.set("Authorization", authHeader);
    if (typeof config.headers.has === "function" && !config.headers.has("Accept")) {
      config.headers.set("Accept", "application/json");
    }
    return config;
  }

  config.headers = {
    ...config.headers,
    Accept: config.headers?.Accept || "application/json",
    Authorization: authHeader,
  };
  return config;
}

function handleAxiosUnauthorized(error) {
  const status = error?.response?.status;
  const data = error?.response?.data;
  if (isUnauthorizedPayload(status, data)) {
    unauthorizedHandler();
  }
  return Promise.reject(error);
}

function handleAxiosSuccess(response) {
  if (isUnauthorizedPayload(response?.status, response?.data)) {
    unauthorizedHandler();
  }
  return response;
}

export function attachAuthInterceptors(client) {
  if (!client?.interceptors || instrumentedClients.has(client)) return client;
  instrumentedClients.add(client);
  client.interceptors.request.use(applyAxiosAuthHeader);
  client.interceptors.response.use(handleAxiosSuccess, handleAxiosUnauthorized);
  return client;
}

function mergeFetchHeaders(input, init, url) {
  const headers = new Headers(
    init?.headers || (typeof Request !== "undefined" && input instanceof Request
      ? input.headers
      : undefined)
  );
  const next = buildAuthHeaders(url, Object.fromEntries(headers.entries()));
  Object.entries(next).forEach(([key, value]) => {
    if (value != null && value !== "") headers.set(key, value);
  });
  return headers;
}

async function inspectFetchUnauthorized(response) {
  if (!response || !isUnauthorizedPayload(response.status)) {
    return;
  }
  let body = {};
  try {
    body = await response.clone().json();
  } catch {
    body = {};
  }
  if (isUnauthorizedPayload(response.status, body)) {
    unauthorizedHandler();
  }
}

export function patchFetch(fetchImpl) {
  const targetFetch =
    fetchImpl || (typeof window !== "undefined" ? window.fetch : null);
  if (!targetFetch || fetchPatched) return targetFetch;

  originalFetch = targetFetch.bind(
    typeof window !== "undefined" ? window : undefined
  );

  const wrapped = async (input, init = {}) => {
    const url =
      typeof input === "string"
        ? input
        : input?.url || "";

    const options = { ...init };
    if (isVenueManagerApiUrl(url)) {
      options.headers = mergeFetchHeaders(input, init, url);
    }

    const response = await originalFetch(input, options);
    if (shouldAttachAuthHeader(url)) {
      await inspectFetchUnauthorized(response);
    }
    return response;
  };

  fetchPatched = true;
  if (!fetchImpl && typeof window !== "undefined") {
    window.fetch = wrapped;
  }
  return wrapped;
}

export function restoreFetch() {
  if (originalFetch && typeof window !== "undefined") {
    window.fetch = originalFetch;
  }
  fetchPatched = false;
  originalFetch = null;
}

attachAuthInterceptors(axios);
if (typeof window !== "undefined") {
  patchFetch();
}
